import { PaymentTypeStaff } from '@insurance/api/requests'
import { App, Button, ButtonProps, Space } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CashBoxSelect } from '../CashBoxSelect'
import { PaymentExtendType, SelectPaymentType } from './SelectPaymentType'

export type PaymentValue = {
  paymentType: PaymentTypeStaff | PaymentExtendType
  cashBox?: number
}

type Props = {
  price?: string
  isLegalEntity?: boolean
  paymentType?: PaymentValue['paymentType']
  isCreditType?: boolean
  onPay?: (value: PaymentValue) => void
  onChangePaymentType?: (value: PaymentValue['paymentType']) => void
} & ButtonProps

export const PayStaffButton = ({
  onPay,
  onChangePaymentType,
  disabled,
  price,
  isLegalEntity,
  paymentType,
  isCreditType,
  children,
  ...props
}: Props) => {
  const { t } = useTranslation()
  const { modal } = App.useApp()

  const [paymentValue, setPaymentValue] = useState<PaymentValue>({
    paymentType: PaymentTypeStaff.CASH
  })

  const showCashBox = [
    PaymentTypeStaff.CASH,
    PaymentTypeStaff.POS,
    PaymentTypeStaff.CASH_MIA
  ].includes(paymentValue.paymentType as PaymentTypeStaff)

  const disabledPay = (showCashBox && !paymentValue.cashBox) || disabled

  useEffect(() => {
    if (!paymentType) return

    setPaymentValue((v) => ({ ...v, paymentType }))
  }, [paymentType])

  const handlePay = () => {
    const fnPay = () =>
      onPay?.({
        ...paymentValue,
        ...(!showCashBox && { cashBox: undefined })
      })

    if (paymentValue.paymentType === PaymentTypeStaff.POS) {
      const informationModal = modal.confirm({
        title: t('information'),
        content: (
          <span
            dangerouslySetInnerHTML={{
              __html: t('information_about_pos', { price })
            }}
          />
        ),
        okText: t('continue'),
        onOk: () => {
          informationModal.destroy()
          modal.confirm({
            title: t('confirm_transaction'),
            content: (
              <span
                dangerouslySetInnerHTML={{
                  __html: t('confirm_transaction_pos', { price })
                }}
              />
            ),
            okText: t('confirm'),
            onOk: fnPay
          })
        }
      })
    } else {
      fnPay()
    }
  }

  return (
    <Space.Compact>
      <SelectPaymentType
        isCreditType={isCreditType}
        isLegalEntity={isLegalEntity}
        value={paymentValue.paymentType}
        onChange={(paymentType) => {
          setPaymentValue((v) => ({ ...v, paymentType }))
          onChangePaymentType?.(paymentType)
        }}
      />

      {showCashBox && (
        <CashBoxSelect
          available
          activeOnly
          allowClear={false}
          style={{ width: 150 }}
          value={paymentValue.cashBox}
          onChange={(cashBox) => setPaymentValue((v) => ({ ...v, cashBox }))}
        />
      )}

      <Button
        type="primary"
        disabled={disabledPay}
        onClick={handlePay}
        {...props}
      >
        {children ?? t('pay')}
      </Button>
    </Space.Compact>
  )
}
